/**
 * DesktopSignInModalHost — mounts the LARGE in-page sign-in surface over ANY
 * page (2026-08-18 UX overhaul, phase 6). Chat's browser lane dispatches a
 * `prest0n:desktop-signin` CustomEvent instead of handing the user a link to
 * go find — the same modal the Integrations page opens, popped over the
 * conversation, so the user never leaves the chat either.
 *
 * detail: { appName: string; sessionId?: string; customSlug?: string;
 *           signInUrl?: string; workspaceAccountId?: string;
 *           lang?: 'en' | 'es'; scope?: 'personal' | 'organization';
 *           origin?: { threadId: string; resumeText?: string } }
 *
 * ONE MOUNT (2026-09-03 owner directive: the sign-in surface "should both be
 * unified and the same"). The Integrations page used to mount the modal
 * itself, in parallel with this host, and the two drifted: this one carried a
 * handed-off session, a workspace target and the conversation's language;
 * that one carried a scope and an onConnected callback. The page now opens
 * the surface through this event like every other caller and hears about
 * completion through CONNECT_SIGNED_IN_EVENT, so there is one surface with
 * one set of capabilities wherever a user meets it.
 */

import React, { useEffect, useState } from 'react';
import DesktopSignInModal from '../services/composio/panel/DesktopSignInModal';
import { readConnectSignInOrigin, type ConnectSignInOrigin } from '../services/composio/connectSignedInEvent';

export const DESKTOP_SIGNIN_EVENT = 'prest0n:desktop-signin';
/** Late custom-connection slug for an ALREADY-OPEN surface (2026-08-26
 *  latency teardown). The connect flow used to await registration before
 *  opening the modal, spending a round trip behind a button with no pending
 *  state; it now opens first and sends the slug here when it lands. A second
 *  DESKTOP_SIGNIN_EVENT would re-open a surface the user had dismissed, so
 *  this one MERGES and never mounts. */
export const DESKTOP_SIGNIN_SLUG_EVENT = 'prest0n:desktop-signin-slug';

interface SignInRequest {
  appName: string;
  sessionId?: string;
  customSlug?: string;
  signInUrl?: string;
  /** Explicit workstation target (2026-08-23): the org VM the sign-in must
   *  run on when the caller's back-pointer would resolve elsewhere (draft
   *  orgs mid-onboarding). */
  workspaceAccountId?: string;
  /** Language for the surface's code-authored failure lines (2026-09-02).
   *  The lane that opens the surface knows the conversation's language; the
   *  browser's own is the fallback inside the modal. */
  lang?: 'en' | 'es';
  /** Which plane the connection saves onto — the Integrations page's
   *  re-finish of an org connection is the one caller that is not
   *  'personal'. */
  scope?: 'personal' | 'organization';
  /** The conversation this sign-in interrupted, so it can be resumed. */
  origin?: ConnectSignInOrigin;
}

export const DesktopSignInModalHost: React.FC = () => {
  const [request, setRequest] = useState<SignInRequest | null>(null);

  useEffect(() => {
    const onEvent = (e: Event) => {
      const detail = (e as CustomEvent).detail as SignInRequest | undefined;
      if (!detail?.appName) return;
      const signInUrl = detail.signInUrl ? String(detail.signInUrl).trim() : '';
      setRequest({
        appName: String(detail.appName).slice(0, 80),
        sessionId: detail.sessionId ? String(detail.sessionId) : undefined,
        customSlug: detail.customSlug ? String(detail.customSlug) : undefined,
        // https only — the URL seeds prest0n's own browser.
        signInUrl: /^https:\/\//i.test(signInUrl) ? signInUrl.slice(0, 500) : undefined,
        workspaceAccountId: detail.workspaceAccountId ? String(detail.workspaceAccountId).slice(0, 80) : undefined,
        // Only the two languages the code-authored lines exist in; anything
        // else leaves the modal on the browser's own language.
        lang: detail.lang === 'es' || detail.lang === 'en' ? detail.lang : undefined,
        scope: detail.scope === 'organization' ? 'organization' : 'personal',
        // Malformed → no origin. Resuming the WRONG thread is worse than not
        // resuming at all, so this never guesses.
        origin: readConnectSignInOrigin((detail as { origin?: unknown }).origin),
      });
    };
    // Late slug: merge into the OPEN surface only. Same appName guard so a
    // stale registration from a dismissed-then-reopened app can't cross-label
    // the current one. appName/signInUrl/workspaceAccountId are unchanged, so
    // the modal's boot effect (keyed on those) does not restart — the session
    // already in flight is never interrupted by this.
    const onSlug = (e: Event) => {
      const detail = (e as CustomEvent).detail as { appName?: string; customSlug?: string } | undefined;
      const slug = detail?.customSlug ? String(detail.customSlug) : '';
      if (!slug || !detail?.appName) return;
      const forApp = String(detail.appName).slice(0, 80);
      setRequest((prev) => (prev && prev.appName === forApp && !prev.customSlug
        ? { ...prev, customSlug: slug }
        : prev));
    };
    document.addEventListener(DESKTOP_SIGNIN_EVENT, onEvent as EventListener);
    document.addEventListener(DESKTOP_SIGNIN_SLUG_EVENT, onSlug as EventListener);
    return () => {
      document.removeEventListener(DESKTOP_SIGNIN_EVENT, onEvent as EventListener);
      document.removeEventListener(DESKTOP_SIGNIN_SLUG_EVENT, onSlug as EventListener);
    };
  }, []);

  if (!request) return null;
  return (
    <DesktopSignInModal
      appName={request.appName}
      sessionId={request.sessionId}
      customSlug={request.customSlug}
      signInUrl={request.signInUrl}
      workspaceAccountId={request.workspaceAccountId}
      lang={request.lang}
      scope={request.scope}
      origin={request.origin}
      onClose={() => setRequest(null)}
    />
  );
};

export default DesktopSignInModalHost;
